import { Component } from 'react';
import { Typography } from 'components/atoms/Typography';
import { Stack } from 'components/base/layout/Stack';
import { Button } from 'components/atoms/Button';
import { Box } from 'components/base/layout/Box';
import { Logger } from 'utils/logger';
import ErrorImage from 'assets/img/error.svg';
import { primary, text } from 'assets/colors/colors';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';
import { emitFatalLog } from 'utils/observability/logger';
import { serializeError } from 'utils/observability/serializeError';
import { isLocalServer } from 'constant/server';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { getWindowLocationPathname } from 'utils/functions/window';

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Console-only in local dev. OTel emission below handles production.
    // Do NOT pass enableInProduction here — that would call emitErrorLog a
    // second time and create a duplicate OTel record alongside the one below.
    Logger.error({
      message: 'Error caught in Error Boundry: ',
      data: error,
    });

    // Send to SigNoz in all deployed environments (staging, production, etc.).
    // serializeError is required because { ...error } produces {} — Error
    // properties (message, stack, name) are non-enumerable and invisible to spread.
    if (!isLocalServer) {
      emitFatalLog({
        body: {
          message: 'Error caught in Error Boundry: ',
          error: serializeError(error),
        },
        attrs: {
          source: 'ErrorBoundary',
          screenName: getWindowLocationPathname(),
        },
        severityNumber: SeverityNumber.FATAL,
      });
    }

    return {
      hasError: true,
    };
  }

  componentDidCatch(_: Error, errorInfo: { componentStack: string }): void {
    // Console-only in local dev. OTel emission below handles production.
    // Same reasoning as getDerivedStateFromError — no enableInProduction.
    Logger.error({
      message: 'Error Boundry Component Stack: ',
      data: errorInfo?.componentStack,
    });

    // componentStack is a plain string so no serialization needed.
    if (!isLocalServer) {
      emitFatalLog({
        body: {
          message: 'Error Boundry Component Stack: ',
          componentStack: errorInfo?.componentStack,
        },
        attrs: {
          source: 'ErrorBoundary',
          screenName: getWindowLocationPathname(),
        },
      });
    }
    this.setState({ hasError: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Stack
          justifyContent="center"
          width={this.props.width || '100%'}
          height={this.props.height || 'auto'}
          alignItems="center"
          marginTop="10px"
          overflow="auto"
        >
          <Box width="305px">
            <img src={ErrorImage} alt="error" width="100%" height="100%" />
          </Box>
          <Typography
            sx={{ color: primary.main, marginTop: 5, fontWeight: 500 }}
            variant="h5"
          >
            Oops! Something went wrong!
          </Typography>
          <Typography
            sx={{ color: text.primary, marginTop: 2, textAlign: 'center' }}
            variant="body1"
          >
            Looks like this page is having a little digital meltdown. 🫠 <br />
            Try refreshing to cool it down.
          </Typography>
          <Stack flexDirection="row" gap={1.5}>
            <Button
              variant="outlined"
              color="secondary"
              sx={{ borderRadius: '50px', marginTop: 2 }}
              onClick={() => {
                const { referrer } = document;
                const currentDomain = window?.location?.origin;
                if (referrer?.startsWith(currentDomain)) {
                  // Navigate to the previous page if it's within the varicon domain
                  window.history.back();
                } else {
                  // Otherwise, navigate to the homepage
                  window.location.href = '/';
                }
              }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              sx={{ borderRadius: '50px', marginTop: 2 }}
              onClick={() => window?.location?.reload()}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      );
    }

    return this.props.children;
  }
}
